import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminDashboard.css";

function AdminDashboard() {
  const [showMenu, setShowMenu] = useState(false);
  const [todayDeposit, setTodayDeposit] = useState(0);
  const [yesterdayDeposit, setYesterdayDeposit] = useState(0);
  const [todayWithdraw, setTodayWithdraw] = useState(0);
  const [yesterdayWithdraw, setYesterdayWithdraw] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [topSalesOverall, setTopSalesOverall] = useState([]);
  const [topSalesMonth, setTopSalesMonth] = useState([]);
  const [topSalesToday, setTopSalesToday] = useState([]);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // ✅ Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Load dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const depRes = await fetch("https://api.backofficemain.com/get_deposit_stats.php");
      const depData = await depRes.json();

      const wdRes = await fetch("https://api.backofficemain.com/get_withdraw_stats.php");
      const wdData = await wdRes.json();

      const userRes = await fetch("https://api.backofficemain.com/get_active_users.php");
      const userData = await userRes.json();

      const topRes = await fetch("https://api.backofficemain.com/get_top_sales.php");
      const topData = await topRes.json();

      if (depData.success) {
        setTodayDeposit(parseFloat(depData.today));
        setYesterdayDeposit(parseFloat(depData.yesterday));
      }
      if (wdData.success) {
        setTodayWithdraw(parseFloat(wdData.today));
        setYesterdayWithdraw(parseFloat(wdData.yesterday));
      }
      if (userData.success) {
        setActiveUsers(userData.count);
      }
      if (topData.success) {
        setTopSalesOverall(topData.overall || []);
        setTopSalesMonth(topData.month || []);
        setTopSalesToday(topData.today || []);
      }
    } catch (err) {
      console.error("Error loading dashboard data:", err);
    }
  };

  // ✅ Navigation handlers
  const goToAddUser = () => {
    navigate("/users");
  };

  const goToAddStaff = () => {
    navigate("/staff");
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div>
          <h2>Admin Dashboard</h2>
          <p className="subtitle">Welcome to World Leading Invest Dashboard</p>
        </div>

        {/* ➕ Plus Button */}
        <div className="plus-container" ref={menuRef}>
          <button
            className="plus-button"
            onClick={() => setShowMenu(!showMenu)}
          >
            <i className="fas fa-plus"></i>
          </button>

          {showMenu && (
            <div className="plus-dropdown">
              <div className="dropdown-item" onClick={goToAddUser}>
                <i className="fas fa-user-plus"></i> Add User
              </div>
              <div className="dropdown-item" onClick={goToAddStaff}>
                <i className="fas fa-briefcase"></i> Add Staff
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <h4>Today Deposit</h4>
          <p className="value">${todayDeposit.toFixed(2)}</p>
          <div className="summary-footer">
            <span>YESTERDAY: ${yesterdayDeposit.toFixed(2)}</span>
          </div>
        </div>

        <div className="summary-card">
          <h4>Today Withdraw</h4>
          <p className="value">${todayWithdraw.toFixed(2)}</p>
          <div className="summary-footer">
            <span>YESTERDAY: ${yesterdayWithdraw.toFixed(2)}</span>
          </div>
        </div>

        <div className="summary-card">
          <h4>Total Active Users</h4>
          <p className="value">{activeUsers}</p>
        </div>
      </div>

      {/* Ranking Info */}
      <div className="ranking-info">
        <h4>Top Sales Staff</h4>
        <p>
          Ranking shown for <strong>Today</strong>, <strong>This Month</strong>, and{" "}
          <strong>Overall</strong>.
        </p>
      </div>

      {/* Tables */}
      <div className="tables-container">
        <div className="table-box">
          <h4>Top Sales Staff Overall</h4>
          <table>
            <thead>
              <tr>
                <th>Ranking</th>
                <th>Staff Username</th>
                <th>Total Earning</th>
              </tr>
            </thead>
            <tbody>
              {topSalesOverall.length > 0 ? (
                topSalesOverall.map((staff, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{staff.staff_username}</td>
                    <td>${parseFloat(staff.total).toFixed(2)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" style={{ textAlign: "center" }}>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="table-box">
          <h4>Top Sales Staff This Month</h4>
          <table>
            <thead>
              <tr>
                <th>Ranking</th>
                <th>Staff Username</th>
                <th>Total Earning</th>
              </tr>
            </thead>
            <tbody>
              {topSalesMonth.length > 0 ? (
                topSalesMonth.map((staff, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{staff.staff_username}</td>
                    <td>${parseFloat(staff.total).toFixed(2)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" style={{ textAlign: "center" }}>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Today Table */}
      <div className="table-box full-width">
        <h4>Top Sales Staff Today</h4>
        <table>
          <thead>
            <tr>
              <th>Ranking</th>
              <th>Staff Username</th>
              <th>Total Earning</th>
            </tr>
          </thead>
          <tbody>
            {topSalesToday.length > 0 ? (
              topSalesToday.map((staff, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{staff.staff_username}</td>
                  <td>${parseFloat(staff.total).toFixed(2)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" style={{ textAlign: "center" }}>
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminDashboard;
